import { useMemo } from "react";
import { useLang } from "../contexts/LanguageContext";
import { OPEN_INFO, findGuidedFlow, getLocalBankFlow } from "../lib/openaccount";
import { affiliateUrl } from "../lib/affiliates";
import GuidedFlowDialog from "./GuidedFlowDialog";
import {
  Building2,
  Phone,
  ExternalLink,
  FileText,
  Wallet as WalletIcon,
  Clock,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Info,
  Scale,
  Home,
} from "lucide-react";

// Stable shuffle: same optionId always yields same order per session,
// but no provider gets a permanent "first" spot across the app.
function seededShuffle(arr, seed) {
  const out = [...arr];
  let s = 0;
  for (let i = 0; i < seed.length; i += 1) s = (s * 31 + seed.charCodeAt(i)) >>> 0;
  const rand = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// Renders the concrete "how to open it" block for a given investment option.
// - Providers are shuffled so no bank gets a permanent 1st slot.
// - Transparency banner up top.
// - Guided flow CTA appears for ANY provider with a flow available.
// - A "compare before choosing" reminder + local-bank alternative.
export default function OpenAccountBlock({ optionId }) {
  const { t, lang } = useLang();
  const info = (OPEN_INFO[optionId] || {})[lang] || (OPEN_INFO[optionId] || {}).fr;

  // Session-stable shuffle keyed by optionId + a random per-session salt.
  const sessionSalt = useMemo(() => {
    if (typeof window === "undefined") return "";
    let salt = window.sessionStorage?.getItem("sereno_shuffle_salt");
    if (!salt) {
      salt = String(Math.floor(Math.random() * 1e9));
      try {
        window.sessionStorage?.setItem("sereno_shuffle_salt", salt);
      } catch {}
    }
    return salt;
  }, []);
  const providers = useMemo(
    () => (info ? seededShuffle(info.providers, `${optionId}-${sessionSalt}`) : []),
    [info, optionId, sessionSalt]
  );

  if (!info) return null;

  // Collect all guided flows for providers of this option
  const guidedProviders = providers
    .map((p) => ({ p, flow: findGuidedFlow(optionId, p.name) }))
    .filter((x) => !!x.flow);
  const localBankFlow = getLocalBankFlow();

  return (
    <section
      data-testid={`open-block-${optionId}`}
      className="mt-8 rounded-2xl border-2 border-[#A762B1]/25 bg-[#F8EFD9] p-6 sm:p-8"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#A762B1]">
          <Building2 className="h-6 w-6" strokeWidth={2} />
        </div>
        <h3 className="font-serif text-2xl font-semibold text-[#101A42] sm:text-3xl">
          {t("open_title")}
        </h3>
      </div>

      {/* Transparency banner */}
      <div
        data-testid="transparency-banner"
        className="mb-6 rounded-2xl border-2 border-[#101A42]/25 bg-white p-5"
      >
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#101A42]">
          <Info className="h-4 w-4" />
          {t("transparency_title")}
        </div>
        <p className="text-base leading-relaxed text-[#333]">
          {t("transparency_text")}
        </p>
      </div>

      {guidedProviders.length > 0 && (
        <div
          data-testid="guided-flow-cta"
          className="mb-6 space-y-3"
        >
          {guidedProviders.map(({ p, flow }) => (
            <div
              key={p.name}
              data-testid={`guided-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex flex-col gap-3 rounded-2xl border-2 border-[#A762B1] bg-white p-5 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="mt-1 h-6 w-6 shrink-0 text-[#A762B1]" />
                <div>
                  <p className="text-sm font-semibold uppercase tracking-widest text-[#A762B1]">
                    {t("open_guided_available")}
                  </p>
                  <p className="mt-1 text-lg text-[#101A42]">
                    {lang === "fr"
                      ? `Ouvrir chez ${p.name}, écran par écran, avec Sereno à vos côtés.`
                      : `Open at ${p.name}, screen by screen, with Sereno by your side.`}
                  </p>
                </div>
              </div>
              <GuidedFlowDialog
                optionId={optionId}
                providerName={p.name}
                trigger={
                  <button
                    data-testid={`guided-start-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="min-h-[56px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#B775C0]"
                  >
                    {t("open_start_guided")}
                    <ArrowRight className="ml-2 inline-block h-5 w-5" />
                  </button>
                }
              />
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-6">
        {/* Providers */}
        <div>
          <div className="mb-3 flex items-center gap-2 text-base font-semibold uppercase tracking-widest text-[#101A42]">
            <Building2 className="h-4 w-4" />
            {t("open_where")}
          </div>
          <ul
            className="grid gap-3 sm:grid-cols-2"
            data-testid="open-providers-list"
          >
            {providers.map((p, i) => (
              <li
                key={i}
                data-testid={`open-provider-${i}`}
                className="rounded-xl border-2 border-[#E8DFC7] bg-white p-4"
              >
                <p className="font-serif text-lg font-semibold text-[#101A42]">
                  {p.name}
                </p>
                {p.note && (
                  <p className="mt-1 text-sm leading-relaxed text-[#4A4A4A]">
                    {p.note}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {p.url && (
                    <a
                      href={affiliateUrl(p.key, `https://${p.url.replace(/^https?:\/\//, "")}`)}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      data-testid={`open-provider-url-${i}`}
                      className="inline-flex items-center gap-1 rounded-full border-2 border-[#101A42] px-3 py-1.5 text-sm font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {p.url}
                    </a>
                  )}
                  {p.phone && (
                    <a
                      href={`tel:${p.phone.replace(/\s/g, "")}`}
                      data-testid={`open-provider-phone-${i}`}
                      className="inline-flex items-center gap-1 rounded-full border-2 border-[#A762B1] px-3 py-1.5 text-sm font-semibold text-[#A762B1] hover:bg-[#A762B1] hover:text-white"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {p.phone}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Docs + Meta */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border-2 border-[#E8DFC7] bg-white p-4">
            <div className="mb-2 flex items-center gap-2 text-base font-semibold text-[#101A42]">
              <FileText className="h-4 w-4" />
              {t("open_docs")}
            </div>
            <ul className="space-y-1 text-base text-[#101A42]">
              {info.docs.map((d, i) => (
                <li
                  key={i}
                  data-testid={`open-doc-${i}`}
                  className="flex items-start gap-2"
                >
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#A762B1]" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid gap-3">
            <div className="rounded-xl border-2 border-[#E8DFC7] bg-white p-4">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#101A42]">
                <WalletIcon className="h-4 w-4" />
                {t("open_min")}
              </div>
              <p
                data-testid="open-min-amount"
                className="font-serif text-xl font-semibold text-[#101A42]"
              >
                {info.min_amount}
              </p>
            </div>
            <div className="rounded-xl border-2 border-[#E8DFC7] bg-white p-4">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#101A42]">
                <Clock className="h-4 w-4" />
                {t("open_time")}
              </div>
              <p className="text-base text-[#101A42]">
                <span className="font-semibold">{t("open_online")} :</span>{" "}
                {info.time_online}
              </p>
              <p className="text-base text-[#101A42]">
                <span className="font-semibold">{t("open_branch")} :</span>{" "}
                {info.time_branch}
              </p>
            </div>
          </div>
        </div>

        {/* Phrase */}
        <div
          data-testid="open-phrase-box"
          className="rounded-xl border-2 border-[#A762B1]/40 bg-white p-4"
        >
          <div className="mb-2 flex items-center gap-2 text-base font-semibold text-[#A762B1]">
            <MessageSquareText className="h-4 w-4" />
            {t("open_phrase")}
          </div>
          <p className="font-serif text-lg italic leading-relaxed text-[#101A42]">
            « {info.phrase_to_say} »
          </p>
        </div>

        {/* Reassurance */}
        {info.reassurance && (
          <div className="rounded-xl bg-[#101A42] p-4 text-white">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#F8EFD9]">
              <ShieldCheck className="h-4 w-4" />
              {t("open_reassurance")}
            </div>
            <p className="text-base leading-relaxed">{info.reassurance}</p>
          </div>
        )}

        {/* Affiliate transparency notice (French law requirement) */}
        <p
          className="rounded-xl bg-[#F8EFD9] px-4 py-3 text-xs italic leading-relaxed text-[#4A4A4A]"
          data-testid="affiliate-notice"
        >
          {t("affiliate_notice")}
        </p>

        {/* Compare before choosing */}
        <div
          data-testid="compare-block"
          className="rounded-2xl border-2 border-[#E59D32]/40 bg-white p-5"
        >
          <div className="mb-3 flex items-center gap-2 text-base font-semibold text-[#E59D32]">
            <Scale className="h-5 w-5" />
            {t("compare_title")}
          </div>
          <p className="text-base leading-relaxed text-[#333]">{t("compare_intro")}</p>
          <ul className="mt-3 space-y-2 text-base text-[#101A42]">
            {["compare_q1", "compare_q2", "compare_q3", "compare_q4"].map((k, i) => (
              <li key={k} className="flex items-start gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E59D32]" />
                <span>{t(k)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Local bank alternative */}
        {localBankFlow && (
          <div
            data-testid="local-bank-block"
            className="rounded-2xl border-2 border-[#A762B1]/30 bg-white p-5"
          >
            <div className="mb-3 flex items-center gap-2 text-base font-semibold text-[#A762B1]">
              <Home className="h-5 w-5" />
              {t("local_bank_title")}
            </div>
            <p className="text-base leading-relaxed text-[#333]">
              {t("local_bank_text")}
            </p>
            <div className="mt-4">
              <GuidedFlowDialog
                optionId="generic"
                providerName="local_bank"
                trigger={
                  <button
                    data-testid="local-bank-start-btn"
                    className="min-h-[52px] rounded-full border-2 border-[#A762B1] px-5 text-base font-semibold text-[#A762B1] hover:bg-[#A762B1] hover:text-white"
                  >
                    {t("local_bank_cta")}
                    <ArrowRight className="ml-2 inline-block h-4 w-4" />
                  </button>
                }
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
